import { dauerMinutenVon, dauerstufeVon, type Groessendauern } from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import { workspacesQuery, type WorkspaceEintrag } from './queries'

/**
 * **Die Dauer, mit der geplant wird – überall dieselbe**
 * ([Z-19](../../../../docs/phasen/zusatz-19-tagesplan.md) AP 19.1,
 * [E-390](../../../../docs/ENTSCHEIDUNGEN.md#e-390)).
 *
 * Die Zahl entstand für den Tagesplan und wohnte dort auch – in `TagesplanAnsicht.tsx`, als
 * private Funktion. Damit war sie genau an der einen Stelle sichtbar, an der sie **nicht**
 * nachgeprüft werden kann: Im Kalender sieht man, wie lang ein Block ist, aber nicht, woher die
 * Länge kommt. Nils kann also nicht erkennen, womit der Plan rechnet – und die Phasendatei
 * verlangt deshalb den Nachweis *„Die Dauer steht am Aufgabendetail und in «Alle Aufgaben»"*.
 *
 * Dafür muss sie aus dem Tagesplan heraus. Zwei Ansichten, die sie selber ausrechnen, wären zwei
 * Zahlen, die auseinanderlaufen können – und eine Dauer, die an zwei Orten verschieden steht,
 * ist schlimmer als gar keine.
 */

/** Kennung der Arbeitswelt → was eine Größe dort kostet. */
export type Weltdauern = Record<string, Groessendauern>

/** Die Überschreibungen aus der Welt-Liste in die Tafel übersetzen – leere Welten fallen weg. */
export function weltdauern(welten: readonly WorkspaceEintrag[]): Weltdauern {
  const tafel: Weltdauern = {}
  for (const w of welten) {
    const eintrag: Groessendauern = {}
    if (w.dauerKlein) eintrag.klein = w.dauerKlein
    if (w.dauerMittel) eintrag.mittel = w.dauerMittel
    if (w.dauerGross) eintrag.gross = w.dauerGross
    if (w.dauerOhne) eintrag.ohne = w.dauerOhne
    if (Object.keys(eintrag).length > 0) tafel[w.id] = eintrag
  }
  return tafel
}

/** Die Tafel aus der ohnehin geladenen Welt-Liste – `staleTime` dort ist großzügig. */
export function useWeltdauern(): Weltdauern {
  const { data: welten = [] } = useQuery(workspacesQuery)
  return weltdauern(welten)
}

export interface DauerAngabe {
  minuten: number
  /** Woher die Zahl kommt – das ist die eigentliche Auskunft. */
  herkunft: 'geschaetzt' | 'groesse' | 'welt' | 'vorgabe'
}

/**
 * **Die geplante Dauer einer Aufgabe und woher sie stammt.**
 *
 * Die Herkunft ist nicht Schmuck. `2 h` allein lässt offen, ob jemand das geschätzt hat oder ob
 * eine Vorgabe geraten hat – und das ist genau der Unterschied, auf den es ankommt, wenn der
 * Plan nicht aufgeht. [F-93](../../../../docs/OFFENE-FRAGEN.md) ist dieselbe Frage von der
 * anderen Seite: 78 offene Aufgaben haben gar keine Größe, und bis hierher sah man ihnen das
 * nirgends an.
 */
export function dauerAngabe(
  aufgabe: { groesse: string | null; estimateMinutes?: number | null; workspaceId?: string | null },
  tafel: Weltdauern,
): DauerAngabe {
  const vorgabe = (aufgabe.workspaceId && tafel[aufgabe.workspaceId]) || {}
  const minuten = dauerMinutenVon(aufgabe, vorgabe)

  const geschaetzt = aufgabe.estimateMinutes
  if (typeof geschaetzt === 'number' && Number.isFinite(geschaetzt) && geschaetzt > 0) {
    return { minuten, herkunft: 'geschaetzt' }
  }
  const stufe = dauerstufeVon(aufgabe)
  const eigen = vorgabe[stufe]
  if (typeof eigen === 'number' && Number.isFinite(eigen) && eigen > 0) {
    return { minuten, herkunft: 'welt' }
  }
  return { minuten, herkunft: stufe === 'ohne' ? 'vorgabe' : 'groesse' }
}

/**
 * **Minuten als Text** – `45 min`, `2 h`, `1,5 h`.
 *
 * Unter einer Stunde in Minuten, darüber in Stunden: `90 min` liest sich niemand als
 * anderthalb Stunden, und `0,75 h` ist keine Zeitangabe, die ein Mensch benutzt.
 */
export function dauerText(minuten: number): string {
  if (minuten < 60) return `${Math.round(minuten)} min`
  const stunden = minuten / 60
  return `${stunden.toLocaleString('de-DE', { maximumFractionDigits: 1 })} h`
}

/** Der Satz hinter der Zahl – warum sie so lautet. */
export function dauerHerkunftText(herkunft: DauerAngabe['herkunft']): string {
  switch (herkunft) {
    case 'geschaetzt':
      return 'geschätzt'
    case 'welt':
      return 'Vorgabe dieser Arbeitswelt'
    case 'groesse':
      return 'aus der Größe'
    case 'vorgabe':
      return 'nicht bewertet – das ist die Vorgabe, keine Schätzung'
  }
}
